<template>
  <div class="empty v-empty">
    <div class="holder">
      <slot name="icon">
        <div
          v-if="img"
          class="image"
          :style="{ backgroundImage: `url(${img})` }"
        />
        <div
          v-else-if="icon"
          class="icon"
        >
          <el-icon
            color="brand-light"
            :name="iconWithVariant"
            size="72"
          />
        </div>
      </slot>
      <div
        v-if="title || slots.title"
        class="title"
        data-testid="empty-title"
      >
        <slot name="title">
          {{ title }}
        </slot>
      </div>
      <div
        v-if="description || slots.description"
        class="description el-text--body-24"
        data-testid="empty-description"
      >
        <slot name="description">
          {{ description }}
        </slot>
      </div>
      <div
        v-if="($listeners.buttonClick || slots.button) && displayButton"
        class="buttons"
      >
        <slot name="button">
          <el-button
            :data-testid="buttonTestID"
            :disabled="disabled"
            :icon="iconButton"
            :loading="buttonPending"
            :type="buttonType"
            @click="clickButton"
          >
            {{ buttonText }}
          </el-button>
        </slot>
      </div>
      <slot name="content" />
    </div>
  </div>
</template>

<script lang="ts">
  import { computed, defineComponent } from 'vue';

  export default defineComponent({
    name: 'Empty',
    props: {
      img: String,
      icon: String,
      title: String,
      description: String,
      buttonText: {
        type: String,
        default: 'Create'
      },
      buttonType: {
        type: String,
        default: 'secondary'
      },
      buttonTestID: String,
      iconButton: String,
      displayButton: {
        type: Boolean,
        default: true
      },
      disabled: Boolean,
      buttonPending: Boolean
    },
    setup(props, { slots, emit }) {
      /**
       * UI uses two icon naming formats('user-o' and 'i-user-o--24'),
       * Empty component uses 24 variant of item icon
       * Need to transform variant
       * @returns {*}
       */
      const iconWithVariant = computed((): string => {
        const { icon } = props;
        if (icon) {
          const variant = icon.slice(-2);
          return ['16', '24', '32', '96'].indexOf(variant) > -1 ? icon : `i-${icon}--24`;
        }
        return '';
      });

      function clickButton(evt): void {
        emit('buttonClick', evt);
      }

      return {
        slots,
        clickButton,
        iconWithVariant
      };
    }
  });
</script>

<style lang="scss" scoped>
  .empty {
    height: 100%;
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 360px;

    .holder {
      flex: 0 1 auto;
      text-align: center;
      max-width: 565px;
      margin: 24px 0;
    }

    .image {
      display: inline-block;
      width: 72px;
      height: 72px;
      background-position: center;
      background-repeat: no-repeat;
    }

    .title {
      line-height: $av-line-height-s;
      font-size: $av-font-size-m;
      color: $av-fixed-primary;
      margin: 16px 0 8px;
    }

    .description {
      max-width: 532px;
      margin: 8px 0 24px;
    }

    .buttons {
      margin-top: 24px;
    }
  }
</style>
